import { Feather } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { appConfig } from '_/config/app';
import { fontFamily, fontSizes } from '_/config/theme';
import themes from '_/constants/themes';
import { useNotificationsContext } from '_/hooks/NotificationsProvider';
import { AppRoute, ProfileProps } from '_/routes/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { useSelector } from 'react-redux';

import SwitchCompany from './SwitchCompany';
import Text from '../Text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const LIMIT_OF_NOTIFICATIONS = 99;

export default function TopMenu() {
  const { navigate } = useNavigation<ProfileProps>();
  const route = useRoute();

  const isPersonal = useSelector((state: any) => state.auth.personal);
  const user = useSelector((state: any) => state.user);
  const { name: companyName, hostSector, pictureUrl } = useSelector((state: any) => state.company);
  const { t: translate } = useTranslation();

  const { notificationsUnread } = useNotificationsContext();
  const insets = useSafeAreaInsets();

  function handleShowNotification() {
    if (route.name === AppRoute.PROFILE) {
      navigate(AppRoute.NOTIFICATION);
      return;
    }
    navigate(AppRoute.NOTIFICATION);
  }

  function renderProfile() {
    const displayName = isPersonal ? user.name : companyName || appConfig.company;

    return (
      <View style={styles.profileWrapper}>
        <Image
          resizeMode="contain"
          style={styles.companyLogo}
          source={
            isPersonal
              ? user.pictureUrl
                ? { uri: pictureUrl }
                : themes.images.avatarSquare
              : pictureUrl
              ? { uri: pictureUrl }
              : themes.images.logo
          }
        />

        <Text style={styles.companyName} numberOfLines={2}>
          {displayName} <Feather name="chevron-down" size={18} style={styles.switchIcon} />
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[
        {
          paddingTop: insets.top,
          backgroundColor: route.name === AppRoute.PROFILE ? themes.colors.white : 'transparent',
        },
      ]}
    >
      <View style={styles.container}>
        <View style={styles.innerContainer}>
          <SwitchCompany buttonStyles={styles.titleContainer} content={renderProfile()} />

          {!isPersonal && (
            <TouchableOpacity
              hitSlop={{ top: 20, left: 20, bottom: 20, right: 20 }}
              onPress={handleShowNotification}
              style={styles.notificationButton}
            >
              <View>
                <Feather
                  name="bell"
                  size={22}
                  style={[
                    styles.notificationIcon,
                    notificationsUnread?.length > 0 && styles.notificationActive,
                  ]}
                />

                {notificationsUnread?.length > 0 && <View style={styles.notificationDot} />}
              </View>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    justifyContent: 'center',
    height: 80,
    paddingHorizontal: 20,
  },
  innerContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  titleContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  rowContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    maxWidth: '100%',
  },
  subtitle: {
    fontFamily: fontFamily.regular,
    fontSize: 13,
    color: themes.colors.graySecondary,
  },
  profileWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '100%',
  },
  companyLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  companyName: {
    fontFamily: fontFamily.semibold,
    fontSize: 14.5,
    color: themes.colors.grayPrimary,
    marginRight: 6,
    flexShrink: 1,
  },
  switchIcon: {
    color: themes.colors.graySecondary,
  },
  notificationButton: {
    marginLeft: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationIcon: {
    color: themes.colors.grayPrimary,
  },
  notificationActive: {
    color: themes.colors.primary,
  },
  notificationDot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: themes.colors.primary,
  },
});
